import React from 'react';
import { cn } from '@/shadcn/lib/utils';
import type { SocialNetwork } from '@/applications/Social/types/social-network';
import { PlatformIcon } from './platform-icon';

export interface SheetTopBarPlatformInfoProps {
  /** The platform to display (e.g., 'instagram', 'tiktok', 'youtube') */
  platform: SocialNetwork;
  /** Optional additional CSS classes */
  className?: string;
  /** Whether the platform info should be clickable (for profile links) */
  clickable?: boolean;
  /** Optional click handler for when clickable is true */
  onClick?: () => void;
  /** Optional username/handle to display when clickable is true */
  username?: string;
  /** Optional children to display after the platform info */
  children?: React.ReactNode;
}

/**
 * SheetTopBarPlatformInfo
 * ------------------------
 * Displays platform icon and post type information in the sheet top bar.
 * Can be rendered as either a simple display or a clickable button.
 *
 * Used across all sheet components for consistent platform information display.
 */
const SheetTopBarPlatformInfo: React.FC<SheetTopBarPlatformInfoProps> = ({
  platform,
  className,
  clickable = false,
  onClick,
  username,
  children,
}) => {
  if (clickable && onClick) {
    return (
      <button
        className={cn(
          'inline-flex items-center gap-2 px-3 py-1 rounded-lg border border-gray-200 bg-white',
          'hover:bg-gray-50 transition-colors',
          className
        )}
        onClick={onClick}
        aria-label={`Open profile for ${username || platform}`}
      >
        <PlatformIcon platform={platform} />
        {username && <span>{username}</span>}
        {children}
      </button>
    );
  }

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <PlatformIcon platform={platform} />
      {children}
    </div>
  );
};

export default SheetTopBarPlatformInfo;
